import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Text, useColorModeValue, Input, InputGroup, InputLeftElement } from "@chakra-ui/react";
import { Link, useLoaderData } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchPosts } from "../github";

const JF = "'Josefin Sans', sans-serif";
const PJ = "'Plus Jakarta Sans', sans-serif";

const PAGE_SIZE = 6;

const PLACEHOLDERS = [
  "linear-gradient(145deg,#0c1520 0%,#1a2d44 100%)",
  "linear-gradient(145deg,#120a1a 0%,#2a1040 100%)",
  "linear-gradient(145deg,#0a1a0a 0%,#142814 100%)",
  "linear-gradient(145deg,#1a1008 0%,#302010 100%)",
  "linear-gradient(145deg,#1a0808 0%,#301010 100%)",
  "linear-gradient(145deg,#0a0a1a 0%,#101030 100%)",
];

function BlogCard({ post, index, colors }) {
  const [hovered, setHovered] = useState(false);

  const { title, description, headerImage } = post.fields;
  const slug = post.slug;
  const placeholder = PLACEHOLDERS[index % PLACEHOLDERS.length];

  const d = new Date(post.sys.createdAt);
  const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <Link to={`/posts/${slug}`} style={{ textDecoration: "none" }}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        bg={colors.cardBg}
        border="1px solid"
        borderColor={hovered ? colors.borderHover : colors.border}
        borderRadius="14px"
        overflow="hidden"
        cursor="pointer"
        sx={{
          transform: hovered ? "translateY(-3px)" : "translateY(0)",
          boxShadow: hovered ? colors.shadowHover : colors.shadow,
          transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        }}
      >
        {/* Top bar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px="14px"
          pt="13px"
          pb="10px"
        >
          <Box display="flex" alignItems="center" gap="6px">
            <i className="ri-article-line" style={{ fontSize: "12px", color: colors.meta }} />
            <Text fontFamily={JF} fontSize="12px" letterSpacing="0.06em" color={colors.meta}>
              {dateStr}
            </Text>
          </Box>
          <Text color={colors.meta} fontSize="15px" letterSpacing="0.12em" lineHeight={1}>
            •••
          </Text>
        </Box>

        {/* Image */}
        <Box px="12px">
          <Box
            position="relative"
            w="100%"
            h={{ base: "220px", sm: "175px" }}
            borderRadius="8px"
            overflow="hidden"
            sx={{ background: placeholder }}
          >
            {headerImage && (
              <Box
                as="img"
                src={headerImage}
                alt={title}
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                w="100%"
                h="100%"
                objectFit="contain"
                display="block"
                sx={{
                  transition: "transform 0.45s ease",
                  transform: hovered ? "scale(1.05)" : "scale(1)",
                }}
              />
            )}

          </Box>
        </Box>

        {/* Footer */}
        <Box px="14px" pt="12px" pb="15px">
          <Text
            fontFamily={PJ}
            fontSize="15px"
            fontWeight="600"
            color={colors.title}
            lineHeight={1.4}
            mb={description ? "8px" : 0}
          >
            {title}
          </Text>
          {description && (
            <Text
              fontFamily={PJ}
              fontSize="13px"
              color={colors.meta}
              lineHeight="1.6"
              sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {description}
            </Text>
          )}
        </Box>
      </Box>
    </Link>
  );
}

export default function Blogs() {
  const posts = useLoaderData();
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  const allTags = [...new Set(posts.flatMap((p) => p.fields.tags ?? []))].sort();

  const filteredPosts = posts.filter(({ fields }) => {
    const matchesQuery = query.trim()
      ? fields.title?.toLowerCase().includes(query.toLowerCase()) ||
        fields.description?.toLowerCase().includes(query.toLowerCase())
      : true;
    const matchesTag = activeTag ? fields.tags?.includes(activeTag) : true;
    return matchesQuery && matchesTag;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cardBg      = useColorModeValue("#ffffff", "#161b22");
  const border      = useColorModeValue("#ebebeb", "#252b33");
  const borderHover = useColorModeValue("#cccccc", "#3a424e");
  const shadow      = useColorModeValue(
    "0 1px 4px rgba(0,0,0,0.06)",
    "0 1px 6px rgba(0,0,0,0.35)"
  );
  const shadowHover = useColorModeValue(
    "0 8px 28px rgba(0,0,0,0.10)",
    "0 8px 28px rgba(0,0,0,0.55)"
  );
  const titleColor   = useColorModeValue("#111111", "#eeeeee");
  const metaColor    = useColorModeValue("#aaaaaa", "#555555");
  const headingColor = useColorModeValue("#111111", "#eeeeee");
  const subColor     = useColorModeValue("#888888", "#777777");
  const spinnerColor   = useColorModeValue("#cccccc", "#444444");
  const inputBg        = useColorModeValue("#f5f5f5", "#1a1f27");
  const inputBorder    = useColorModeValue("#e2e2e2", "#252b33");
  const inputFocusBorder = useColorModeValue("#888888", "#555555");
  const inputColor     = useColorModeValue("#111111", "#eeeeee");
  const placeholderColor   = useColorModeValue("#aaaaaa", "#555555");
  const tagBg              = useColorModeValue("#f0f0f0", "#1e242c");
  const tagBorder          = useColorModeValue("#e0e0e0", "#2e343e");
  const tagColor           = useColorModeValue("#555555", "#888888");
  const tagActiveBg        = useColorModeValue("#111111", "#eeeeee");
  const tagActiveColor     = useColorModeValue("#ffffff", "#111111");

  const colors = {
    cardBg,
    border,
    borderHover,
    shadow,
    shadowHover,
    title: titleColor,
    meta: metaColor,
  };

  // Reset pagination when filters change
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [query, activeTag]);

  const hasMore = !query.trim() && !activeTag && visible < posts.length;

  const loadMore = useCallback(() => {
    setVisible((v) => Math.min(v + PAGE_SIZE, filteredPosts.length));
  }, [filteredPosts.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return (
    <Box as="section" pt={14} pb={16}>
      <Helmet>
        <title>Blog | Huy Phu</title>
        <meta name="description" content="Articles on security engineering, QA, penetration testing, and AI by Huy Phu." />
        <meta property="og:title" content="Blog | Huy Phu" />
        <meta property="og:description" content="Articles on security engineering, QA, penetration testing, and AI by Huy Phu." />
        <meta property="og:url" content="https://hqphu.com/posts" />
      </Helmet>

      {/* Heading */}
      <Text
        fontFamily={PJ}
        fontSize="34px"
        fontWeight="700"
        color={headingColor}
        lineHeight={1.2}
        mb={2}
      >
        Blog
      </Text>
      <Text
        fontFamily={PJ}
        fontSize="15px"
        color={subColor}
        mb={8}
      >
        Writing on cybersecurity, engineering, and the tools I build.
      </Text>

      {/* Search */}
      <InputGroup mb={allTags.length > 0 ? 4 : 10}>
        <InputLeftElement pointerEvents="none" h="42px">
          <i className="ri-search-line" style={{ fontSize: "15px", color: placeholderColor }} />
        </InputLeftElement>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search posts…"
          h="42px"
          bg={inputBg}
          border="1px solid"
          borderColor={inputBorder}
          borderRadius="10px"
          color={inputColor}
          fontFamily={PJ}
          fontSize="14px"
          _placeholder={{ color: placeholderColor }}
          _focus={{ borderColor: inputFocusBorder, boxShadow: "none" }}
          _hover={{ borderColor: inputFocusBorder }}
        />
      </InputGroup>

      {/* Tags */}
      {allTags.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap="8px" mb={10}>
          {allTags.map((tag) => {
            const isActive = activeTag === tag;
            return (
              <Box
                key={tag}
                as="button"
                onClick={() => setActiveTag(isActive ? null : tag)}
                fontFamily={JF}
                fontSize="11px"
                letterSpacing="0.1em"
                px="12px"
                py="5px"
                borderRadius="20px"
                border="1px solid"
                borderColor={isActive ? "transparent" : tagBorder}
                bg={isActive ? tagActiveBg : tagBg}
                color={isActive ? tagActiveColor : tagColor}
                cursor="pointer"
                sx={{ transition: "all 0.15s ease" }}
                _hover={{
                  borderColor: isActive ? "transparent" : inputFocusBorder,
                  color: isActive ? tagActiveColor : inputColor,
                }}
              >
                #{tag}
              </Box>
            );
          })}
        </Box>
      )}

      {/* 2-column grid (1-col on mobile) */}
      {filteredPosts.length === 0 ? (
        <Text fontFamily={PJ} fontSize="15px" color={subColor} mt={4}>
          No posts match{query.trim() ? ` "${query}"` : ""}{activeTag ? ` in #${activeTag}` : ""}.
        </Text>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
          gap="16px"
        >
          {(query.trim() ? filteredPosts : filteredPosts.slice(0, visible)).map((post, i) => (
            <BlogCard
              key={post.sys.id}
              post={post}
              index={i}
              colors={colors}
            />
          ))}
        </Box>
      )}

      {/* Sentinel / loader */}
      <Box ref={sentinelRef} mt={10} display="flex" justifyContent="center">
        {hasMore && (
          <Box
            w="20px"
            h="20px"
            borderRadius="full"
            border="2px solid"
            borderColor={spinnerColor}
            borderTopColor="transparent"
            sx={{ animation: "spin 0.8s linear infinite" }}
          />
        )}
      </Box>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

export const blogsLoader = async () => {
  return fetchPosts();
};
